import React, { useEffect, useState } from 'react'

import { graphql, Link } from 'gatsby'
import styled from 'styled-components'

import Layout from '../../components/core/layout'
import SEO from '../../components/core/seo'
import AccreditedYouBet from '../../components/cross-template-statics/AccreditedYouBet'
import ProgramSelector from '../../components/cross-template-statics/ProgramSelector'
import Footnote from '../../components/elements/Footnote'
import HeroInternal from '../../components/elements/HeroInternal'
import FormCampusSelect from '../../components/forms/FormCampusSelect'
import FormWrapperGray from '../../components/forms/FormWrapperGray'
import TestimonialSlider from '../../components/reusables/TestimonialSlider'
import { colors } from '../../style/design-system'

type ProgramsPageProps = {
  className?: string
  /** Passed by Gatsby to the page component via the query below. */
  data: {
    heroImgLg: {
      childImageSharp: { gatsbyImageData: any }
      publicURL: string
    }
  }
  /** Passed by Gatsby to the page component. */
  location: {
    hash: string
    host: string
    hostname: string
    href: string
    key: string
    origin: string
    pathname: string
    port: string
    protocol: string
    search: string
    /** Can provide in the Gatsby Link component:
     * https://www.gatsbyjs.com/docs/location-data-from-props/
     */
    state: { campus: string; key: string }
  }
}

const Programs = ({ className, data, location }: ProgramsPageProps) => {
  const [campusCode, setCampusCode] = useState('45')

  useEffect(() => {
    if (location?.state?.campus) setCampusCode(location.state.campus)
    else {
      const params = new URLSearchParams(location.search)
      const campusAlias = params.get('c')
      if (campusAlias === 'online') setCampusCode('45')
      else if (campusAlias === 'clearwater') setCampusCode('47')
    }
  }, [location.search, location.state])

  return (
    <Layout>
      <SEO
        useUMAAcronym
        title="Online and On-Campus Healthcare Diploma & Degree Programs "
        description="Earn an education that could open the door to a brighter future. Learn more about our online and campus-based healthcare programs and degrees at Ultimate Medical Academy now."
        ogImage={data.heroImgLg.publicURL}>
        <link rel="canonical" href="https://www.ultimatemedical.edu/future-students/programs/" />
      </SEO>

      <div className={`${className} list-style-ascii-chevron`}>
        <HeroInternal
          backgroundImage={{
            image: data.heroImgLg.childImageSharp.gatsbyImageData,
          }}
          contentLg="Healthcare Degree Programs"
          contentSm="Your education. Your future."
        />
        <div className="section--hero-mobile d-lg-none">
          <div className="container">
            <h1 className="mt-8">Healthcare Degree Programs</h1>
          </div>
        </div>
        <main>
          <div className="section--content">
            <div className="container text-lg-center my-6 mb-9 mb-lg-10 mt-lg-0">
              <div className="row">
                <div className="col-lg-10 offset-lg-1">
                  <h2 className="h2 blue mt-lg-10">Enroll today to earn a degree in healthcare.</h2>
                  <p>
                    Ultimate Medical Academy&apos;s healthcare degree programs are designed to offer the quality
                    education you need to confidently step into the field. From the moment you enroll &mdash; and even
                    through the first few weeks of class &mdash; you&apos;ll have a team at UMA committed to your
                    success to guide you along the way. Our associate degree and diploma programs will help you pursue a
                    career you can be proud of in a field people rely on.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <ProgramSelector initialCampus={campusCode} />

          <div className="container container--sm text-left py-9">
            <h2>Pick from online and on-campus classroom settings.</h2>
            <p>
              Classes at UMA can be completed either online or in campus-based programs at our Clearwater campus. Our
              healthcare degree programs offer the flexibility to get an education whichever way best fits you, whether
              that’s from the comfort of home or through campus-based learning. Head to our online and Clearwater campus
              pages below to see full program listings and learn more about what to expect from each.
            </p>
            <ul>
              <li>
                <Link to="/campuses/online/">Learn About the Online Campus</Link>
              </li>
              <li>
                <Link to="/campuses/clearwater/">Learn About the Clearwater Campus</Link>
              </li>
            </ul>
          </div>
          <FormWrapperGray bgColor={colors.dev.blue.d()} textColor="#fff">
            <div className="container">
              <FormCampusSelect includeUserErrorModal className="inline-form" />
            </div>
          </FormWrapperGray>
          <div className="bg-dark-blue py-9">
            <div className="container container--sm  text-left">
              <h2 className="white">Why choose a degree in healthcare?</h2>
              <p className="white">
                From job security to simply helping other people, there are plenty of reasons to want to work in the
                healthcare industry.
              </p>
              <p className="white">
                Healthcare occupations are expected to grow 16 percent from 2020 - 2030, according to the Bureau of
                Labor Statistics (BLS). This is faster than the average growth rate for all other occupations. In fact,
                the healthcare industry is projected to have 16.97 million open positions from 2020 - 2030.
                <Footnote
                  content={
                    '{{"shortcode": "footnote", "content": "${bls.healthcare.growthPercent.sourcePrefix}", "link": "${bls.healthcare.growthPercent.source}"}}'
                  }
                />
              </p>
              <p className="white">
                In addition to growing healthcare careers, another reason this industry is one to consider is because
                you can have a wide variety of potential opportunities for where you can pursue work.
              </p>
              <p className="white">Take a look at the articles below for more information.</p>
              <ul>
                <li className="white">
                  <Link className="white" to="/blog/7-reasons-why-you-should-pursue-a-career-in-healthcare/">
                    7 Reasons Why You Should Pursue a Career in Healthcare
                  </Link>
                </li>
                <li className="white">
                  <Link className="white" to="/blog/healthcare-jobs-that-help-people/">
                    8 Jobs That Help People: Healthcare Focused Careers
                  </Link>
                </li>
                <li className="white">
                  <Link className="white" to="/blog/fastest-growing-healthcare-careers/">
                    10 Fast-Growing Careers in Healthcare
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="container container--sm text-lg-left py-9 py-lg-10">
            <h2 className="blue mt-0">Which program is right for you?</h2>
            <p>
              If you know you want to enter a career in healthcare but aren’t sure which position would suit you best,
              you can try our fun and informal <Link to="/program-quiz/">healthcare career quiz</Link> for inspiration.
            </p>
          </div>
          <TestimonialSlider testimonials={data.testimonials.edges} heading="Reviews and testimonials" />
          <div className="section--accredited text-white text-center">
            <AccreditedYouBet />
          </div>
        </main>
      </div>
    </Layout>
  )
}

Programs.defaultProps = {
  className: '',
}

export default styled(Programs)`
  @media (min-width: 992px) {
    h1,
    .h1 {
      font-size: 3rem;
    }
  }
`

export const query = graphql`
  {
    heroImgLg: file(absolutePath: { regex: "/heroes/programs-listing.jpg/" }) {
      childImageSharp {
        gatsbyImageData(quality: 90, width: 1419, placeholder: NONE, layout: FIXED)
      }
      publicURL
    }
    testimonials: allWpTestimonial(
      filter: { uri: { regex: "/leticia-b|jennifer-w|dominique-b|joshua-a|angelica-v|miguel-r|heather-r|brittney-j/" } }
    ) {
      edges {
        node {
          metaReviewerName
          metaReviewerVideo
          excerpt
          featuredImage {
            node {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 700, placeholder: NONE, layout: CONSTRAINED)
                }
              }
            }
          }
        }
      }
    }
  }
`
